/**
 * The frame every Daily Brief widget sits in: the title bar (the drag handle
 * on the desktop board), the widget's own controls, the live dot, × to remove,
 * and the body that scrolls when the widget is smaller than its content.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import type { BriefWidget } from "@qufolio/model";
import type { BriefData } from "./useBriefData";

/** What the board hands every widget. */
export interface WidgetProps {
  widget: BriefWidget;
  /** Writes through `setWidgetSettings`; absent on a read-only board. */
  onSettings?: (settings: Record<string, unknown>) => void;
  onRemove?: () => void;
}

/**
 * One of a widget's settings (`source`, `mode`, `groupBy`, `sort`). On an
 * editable board the config is the truth, so Reset to preset puts a toggle
 * back; on a read-only one the choice is this view's own and is not kept.
 */
export function useWidgetSetting<T>(
  widget: BriefWidget,
  key: string,
  fallback: T,
  onSettings?: (settings: Record<string, unknown>) => void,
): [T, (v: T) => void] {
  const [local, setLocal] = useState<T | undefined>(undefined);
  const stored = widget.settings?.[key] as T | undefined;
  const value = onSettings ? (stored ?? fallback) : (local ?? stored ?? fallback);
  const set = (v: T) => (onSettings ? onSettings({ [key]: v }) : setLocal(v));
  return [value, set];
}

/** The class `react-grid-layout` drags by, and the one it never starts a drag from. */
export const DRAG_HANDLE = "qf-brief-drag";
export const NO_DRAG = "qf-brief-nodrag";

export function WidgetCard({
  title, subtitle, controls, live, onRemove, children,
}: {
  title: string;
  /** Beside the title, before the spacer ("vs SPY"); the controls go on the right. */
  subtitle?: ReactNode;
  controls?: ReactNode;
  live?: boolean;
  /** Absent on a read-only board. */
  onRemove?: () => void;
  children: ReactNode;
}) {
  return (
    <div style={CARD} data-testid="brief-widget" aria-label={title}>
      <div className={DRAG_HANDLE} style={onRemove ? { ...HEAD, cursor: "move" } : HEAD}>
        <span style={TITLE}>{title}</span>
        {subtitle}
        {live && <span style={LIVE_DOT} title="Live — today's bar, moving with the market" role="img" aria-label="live" />}
        <span style={{ flex: 1 }} />
        {controls}
        {onRemove && (
          <button type="button" className={NO_DRAG} aria-label={`Remove ${title}`} title="Remove"
            onClick={onRemove} style={REMOVE}>
            ×
          </button>
        )}
      </div>
      <div style={BODY}>{children}</div>
    </div>
  );
}

/** "loading…" until the first reply, the server's own sentence on a failure, else the body. */
export function DataState<T>({ state, children }: { state: BriefData<T>; children: (data: T) => ReactNode }) {
  return (
    <>
      {state.error && <div style={ERROR} role="alert">{state.error}</div>}
      {state.data ? children(state.data) : !state.error && <div style={NOTE}>loading…</div>}
    </>
  );
}

const CARD: CSSProperties = {
  height: "100%", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflow: "hidden",
  border: "1px solid var(--qf-border)", borderRadius: 10, background: "var(--qf-background)", color: "var(--qf-text)",
};
const HEAD: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", flex: "none", userSelect: "none",
  background: "var(--qf-surface)", borderBottom: "1px solid var(--qf-border)", flexWrap: "wrap",
};
const TITLE: CSSProperties = { fontWeight: 600, fontSize: 15, whiteSpace: "nowrap" };
const LIVE_DOT: CSSProperties = {
  width: 7, height: 7, borderRadius: "50%", background: "var(--qf-positive)", flex: "0 0 auto",
};
const REMOVE: CSSProperties = {
  background: "none", border: "none", color: "var(--qf-text-muted)", cursor: "pointer", fontSize: 17,
  padding: "0 4px", lineHeight: 1,
};
// `containerType` lets a widget's own width drive its layout (`cells.BAR_HIDE_CSS`).
const BODY: CSSProperties = {
  padding: "6px 10px", overflow: "auto", flex: 1, minHeight: 0, fontSize: 13, containerType: "inline-size",
};
const ERROR: CSSProperties = { color: "var(--qf-negative)", fontSize: 13, padding: "2px 0 6px" };
const NOTE: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 13 };
